import { Modal } from "flowbite-react";
import { Form, FormikProvider, useFormik } from "formik";
import ButtonWithLoader from "../../../../../../../common/components/ui/loading/ButtonLoading";
import * as Yup from "yup";
import { useToastContext } from "../../../../../../../common/components/ui/toast/toast.context";
import { createFineAssasSubscription } from "../../../../../services/integration.api";
import { useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { numberToCurrency } from "../../../../../../../common/helpers/currency.helpers";
import ComboBoxFormik from "../../../../../../../common/components/form/ComboBoxFormik";
import { PaymentInfo } from "../../../../../../../types/payment.type";
import { InstallmentOption, useCalculateInstallmentOptions } from "../../../../../helpers/calculationUtils";
import { groupFinesByPlate } from "../../../../../helpers/methods/dataExtractDescription";
import { NegotiationFineForm } from "../form/FormNegotiationFine";
import { FormBillingFine } from "../form/FormBilingFine";

function isTodayOrFuture(dateString) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const inputDate = new Date(dateString + 'T00:00:00');
  inputDate.setHours(0, 0, 0, 0);


  return inputDate >= today;
}

const parseMoneyValue = (value: unknown): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value !== 'string') {
    return 0;
  }

  const normalized = value
    .replace(/[R$\s]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

type Props = {
  onClosed: () => void;
  open: boolean
  trafficFines: PaymentInfo[]
}

export function ModalFine(props: Props) {
  const [selectedPlate, setSelectedPlate] = useState<string>("");
  const [selectedAction, setSelectedAction] = useState("");
  const [descriptionEdited] = useState(false);

  const handleCloseModal = () => { props.onClosed && props.onClosed() };
  const { notifyError, notifySuccess } = useToastContext();
  const { renterId } = useParams()
  const [, setSelectedInstallmentOption] = useState('0');
  const trafficFines = props.trafficFines;


  const validationSchema = Yup.object().shape({
    value: Yup.string().required('Valor da multa é obrigatório'),
    entryValue: Yup.number()
      .transform((_value, originalValue) => {
        if (originalValue === '' || originalValue === null || originalValue === undefined) {
          return 0;
        }
        return parseMoneyValue(originalValue);
      })
      .min(0, 'Valor de entrada inválido')
      .when('actionSelection', {
        is: (value) => value === 'NegotiationFine',
        then: (schema) => schema.max(Yup.ref('value'), 'Entrada não pode ser maior que o valor total'),
      }),
    licensePlate: Yup.string().required('Placa do veículo é obrigatório'),
    installmentOption: Yup.string().when('actionSelection', {
      is: (value) => value === 'NegotiationFine',
      then: (schema) => schema.required('Período de cobrança é obrigatório'),
    }),
    description: Yup.string().required('Descrição da multa é obrigatório'),
    nextDueDate: Yup.string().required('Data da primeira parcela é obrigatório').test('nextDueDate',
      'A data não pode ser anterior a hoje.', value => isTodayOrFuture(value)),
    amoutTrafficNumbers: Yup.string().when('actionSelection', {
      is: (value) => value === 'NegotiationFine',
      then: (schema) => schema.required('Número do auto de infração é obrigatório'),
    }),
    trafficNumbers: Yup.string().when('actionSelection', {
      is: (value) => value === 'BillingFine',
      then: (schema) => schema.required('Número do auto de infração é obrigatório'),
    }),
    infractionDate: Yup.string().when('actionSelection', {
      is: (value) => value === 'BillingFine',
      then: (schema) => schema.required('Número do auto de infração é obrigatório'),
    }),
    valueTrafficFine: Yup.string().when('actionSelection', {
      is: (value) => value === 'BillingFine',
      then: (schema) => schema.required('Número do auto de infração é obrigatório'),
    }),

  });

  const formik = useFormik({
    initialValues: {
      value: 0,
      entryValue: 0,
      remainingValue: 0,
      installmentValue: "",
      licensePlate: "",
      description: `PARCELAMENTO DE MULTAS DE TRANSITO DA 
      PLACA: ${selectedPlate} - REFERENTE AO AUTO DE INFRAÇÃO Nº: `,
      descriptionTrafficFine: ``,
      trafficNumbers: ``,
      infractionDate: "",
      renterId: renterId || "",
      installmentOption: "",
      nextDueDate: "",
      paymentsIds: [],
      amoutTrafficNumbers: "",
      actionSelection: selectedAction
    },

    onSubmit: async (values) => {
      try {
        const payload = {
          ...values,
          value: values.actionSelection === 'NegotiationFine'
            ? parseMoneyValue(values.remainingValue)
            : parseMoneyValue(values.value),
        };

        const { ok, message } = await createFineAssasSubscription(payload)

        if (!ok && message) {
          notifyError(message);
        }

        if (ok) {
          notifySuccess("Multa criada com sucesso");
        }
      } catch (e) {
        console.error(e);
      }
    },
    validationSchema
  });

  const filteredTrafficFines = trafficFines.filter(
    (fine) => !fine.planId || !fine.planId.toUpperCase().includes("NEGOTIATIONFINE")
  );

  const groupedFines = groupFinesByPlate(filteredTrafficFines);

  const valueFineLicensePlate = groupedFines.reduce((acc, { totalFine }) => acc + totalFine, 0);

  const valueTotalFines =
    formik.values.value > 0 ? formik.values.value : valueFineLicensePlate;

  const remainingValueForInstallments =
    formik.values.actionSelection === 'NegotiationFine'
      ? parseMoneyValue(formik.values.remainingValue)
      : valueTotalFines;


  const calculateInstallmentOptions = useCalculateInstallmentOptions();
  const installmentOptions: InstallmentOption[] = useMemo(() => {
    return calculateInstallmentOptions(remainingValueForInstallments, 20);
  }, [remainingValueForInstallments, calculateInstallmentOptions]);

  useEffect(() => {

    if (selectedPlate) {
      const group = groupedFines.find(g => g.licensePlate === selectedPlate);
      if (group) {
        const autoNumbers = group.autoNumbers.join('/ ');
        formik.setFieldValue('value', group.totalFine);
        formik.setFieldValue('entryValue', 0);
        formik.setFieldValue('remainingValue', group.totalFine);
        formik.setFieldValue('installmentOption', '');
        formik.setFieldValue('installmentValue', '');
        formik.setFieldValue('paymentsIds', group.paymentIds);
        formik.setFieldValue('amoutTrafficNumbers', group.autoNumbers.join('/ '));

        if (!descriptionEdited) {
          const newDescription = `PARCELAMENTO DE MULTAS DE TRANSITO DA PLACA: ${selectedPlate}, REFERENTE AO AUTO DE INFRAÇÃO Nº: ${autoNumbers} NO VALOR DE ${numberToCurrency(group.totalFine)} EM ${formik.values.installmentOption}X`;
          formik.setFieldValue('description', newDescription);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlate, descriptionEdited]);

  useEffect(() => {
    const total = parseMoneyValue(formik.values.value);
    const entry = parseMoneyValue(formik.values.entryValue);
    const remaining = Math.max(total - entry, 0);

    if (parseMoneyValue(formik.values.remainingValue) !== remaining) {
      formik.setFieldValue('remainingValue', remaining);
    }

    if (entry > total) {
      formik.setFieldValue('entryValue', total);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.value, formik.values.entryValue]);

  useEffect(() => {
    const valueSelectedOption = installmentOptions.find(
      (option) => option.value === formik.values.installmentOption
    );
    const installmentAmount = valueSelectedOption?.valueOption || '';
    if (installmentAmount && formik.values.installmentValue !== installmentAmount) {
      formik.setFieldValue('installmentValue', installmentAmount);
    }

    if (formik.values.installmentOption && !valueSelectedOption) {
      formik.setFieldValue('installmentOption', '');
      formik.setFieldValue('installmentValue', '');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installmentOptions, formik.values.installmentOption]);

  useEffect(() => {
    if (selectedAction === 'BillingFine') {
      const placa = formik.values.licensePlate || '';
      const autoInfracao = formik.values.trafficNumbers || '';
      formik.setFieldValue(
        'descriptionTrafficFine',
        `PLACA: ${placa} - REFERENTE AO AUTO DE INFRAÇÃO Nº: ${autoInfracao}`
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.licensePlate, formik.values.trafficNumbers, selectedAction]);

  const handleInstallmentOptionChange = useCallback((option) => {
    setSelectedInstallmentOption(option);
  }, []);

  const handleActionChange = (value: string) => {
    setSelectedAction(value);
  };

  const handleSelectedPlate = (plate: string) => {
    setSelectedPlate(plate);
  }



  return (
    <Modal
      show={props.open}
      size={"4xl"}
      onClose={handleCloseModal}
      className="dark:text-black">
      <Modal.Header className="text-black">
        <div className="flex items-baseline justify-center bg-gray-100">
          <h2 className="ml-2 text-black">Negociar Multa</h2>
        </div>
      </Modal.Header>
      <Modal.Body>
        <FormikProvider value={formik}>
          <Form>
            {/* Mova o ComboBoxFormik para dentro do Form */}
            <ComboBoxFormik
              name="actionSelection"
              label="Selecione a ação:"
              options={[
                { value: "-", label: "-" },
                { value: "BillingFine", label: "Lançar Multas" },
                { value: "NegotiationFine", label: "Negociar Multas" }
              ]}
              required={true}
              onChange={(value) => handleActionChange(value as string)}
              value={selectedAction}
            />

            {/* Mostra conteúdo apenas quando uma opção válida é selecionada */}
            {selectedAction === "NegotiationFine" && (
              <>
                <NegotiationFineForm
                  groupedFines={groupedFines}
                  installmentOptions={installmentOptions}
                  handleInstallmentOptionChange={handleInstallmentOptionChange}
                  handleSelectedPlate={handleSelectedPlate}
                />
                <div className="flex justify-end col-span-5 mt-10">
                  <ButtonWithLoader type="submit" isLoading={formik.isSubmitting}>
                    Enviar
                  </ButtonWithLoader>
                </div>
              </>
            )}

            {selectedAction === "BillingFine" && (
              <>
                <FormBillingFine />
                <div className="flex justify-end col-span-5 mt-10">
                  <ButtonWithLoader type="submit" isLoading={formik.isSubmitting}>
                    Enviar
                  </ButtonWithLoader>
                </div>
              </>
            )}
          </Form>
        </FormikProvider>
      </Modal.Body>
    </Modal>
  );
}